/**
 * Read tools — how the agent finds material (docs/agent.md §3).
 *
 * Query construction is the executor's job, not the model's. The model supplies
 * flat arguments (`query`, `filters: {year: {from, to}}`); this module turns
 * them into a real pan.do/ra `find` query. That split is deliberate: cheap
 * models fumble structured DSLs, but they handle flat key/value arguments well,
 * and every query we build is then correct by construction.
 *
 * The allowed filter keys are NOT hardcoded. They come from each instance's own
 * `site.itemKeys` (pandora `init`), so pad.ma's `project`/`topic`/`location`,
 * indiancine.ma's filmography credits, and 0xdb.org's `votes`/`genre` all work
 * without this file knowing they exist. An unknown key produces an error that
 * lists the real ones — the model corrects itself from that.
 *
 * ---------------------------------------------------------------------------
 * Moment search: two query trees, not one
 * ---------------------------------------------------------------------------
 * `findAnnotations` takes `query` (over annotation keys — value, layer, item)
 * AND `itemsQuery` (over the parent item's metadata — year, director,
 * rightslevel). "red carpet in 1970s films" needs both, and until `itemsQuery`
 * was wired up the year half was silently dropped: there was no argument to
 * put it in, so the model discarded it rather than failing.
 *
 * The boolean grammar (`+` AND, `|` OR, `!` NOT) and the shape it compiles to
 * are taken from https://ctrl-z.in/clips/, which searches the same annotation
 * corpus on the same instances.
 */
import { stripHtml } from '../../api/pandora';
import { describeEdit } from '../context/describeEdit';
import type { ToolResult } from '../core/types';
import { toolError } from '../core/types';
import type { ArchiveAnnotation, ReadContext, ReadToolDef } from './catalog';

/** Text-ish keys we never expose as filters — `query` already covers them. */
const NON_FILTER_KEYS = new Set(['*', 'annotations', 'id']);

/** Per-result text budget, so a 20-hit search cannot blow the context (§7). */
const VALUE_CHARS = 200;
const TRANSCRIPT_CHARS = 4000;
/**
 * What one transcript line costs in the model's context beyond its text: the
 * JSON envelope `{"in":123.4,"out":128.9,"text":"…"},`. Budgeting on raw text
 * alone understated the real cost by about a third, which is the difference
 * between a budget and a guess.
 */
const LINE_ENVELOPE_CHARS = 40;

interface PandoraCondition {
  key: string;
  operator: string;
  value: string | number | boolean;
}

interface ConditionGroup {
  conditions: ConditionNode[];
  operator: '&' | '|';
}

type ConditionNode = PandoraCondition | ConditionGroup;

/** Default and ceiling for how many moments one search pulls back. */
const DEFAULT_MOMENT_LIMIT = 100;
const MAX_MOMENT_LIMIT = 500;

/**
 * Cards written into one chat attachment. This lands in the Y.Doc chat log and
 * stays there for the life of the project, so it is a storage budget, not a
 * display one: 200 cards at ~120 bytes is ~24KB per search. The count above it
 * still reports the true total, so a wider search reads as "showing 200 of
 * 412" rather than silently looking complete.
 */
const MAX_MOMENT_CARDS = 200;

/** Per-card blurb. The full annotation text is one click away in the archive. */
const CARD_NOTE_CHARS = 90;

/** Rows spread across films for the model to work from directly. */
const SAMPLE_ROWS = 10;

/**
 * Split a search string into terms and their boolean operators.
 *
 * `red+carpet|rug !wedding` → AND "red", AND "carpet", OR "rug", NOT "wedding".
 * The first term carries no separator and defaults to AND. Ported from
 * `getSearchQ` in https://ctrl-z.in/clips/ so a query pasted from that page
 * means the same thing here.
 */
export function parseQueryTerms(search: string): { op: '+' | '|' | '!'; str: string }[] {
  const regex = /([+|!])?([^+|!]+)/g;
  const out: { op: '+' | '|' | '!'; str: string }[] = [];
  let match: RegExpExecArray | null;
  while ((match = regex.exec(search)) !== null) {
    const str = match[2].trim();
    if (str) out.push({ op: (match[1] as '+' | '|' | '!') ?? '+', str });
  }
  return out;
}

/**
 * Compile parsed terms into conditions on the annotation `value`.
 *
 * The four branches are ctrl-z's and are deliberately not collapsed into one
 * generic tree: `(or | and) & not` is not the same query as `or | and | not`,
 * and pan.do/ra evaluates exactly what it is handed. `=` is "contains"
 * (case-insensitive), which is what a free-text search wants.
 */
export function valueConditions(
  terms: { op: '+' | '|' | '!'; str: string }[],
): ConditionNode[] {
  const cond = (op: string) => (t: { str: string }): PandoraCondition => ({
    key: 'value',
    operator: op,
    value: t.str,
  });
  const and = terms.filter((t) => t.op === '+').map(cond('='));
  const or = terms.filter((t) => t.op === '|').map(cond('='));
  const not = terms.filter((t) => t.op === '!').map(cond('!='));

  const andGroup: ConditionGroup | null = and.length
    ? { conditions: and, operator: '&' }
    : null;
  const orGroup: ConditionGroup | null = or.length ? { conditions: or, operator: '|' } : null;
  const notGroup: ConditionGroup | null = not.length
    ? { conditions: not, operator: '&' }
    : null;

  // A bare `!term` search has no AND group at all — ctrl-z assumes one always
  // exists because its first term cannot be negated. Emitting `{conditions: []}`
  // would ask the archive to evaluate an empty group, so drop it instead.
  const positive: ConditionNode | null =
    andGroup && orGroup
      ? { conditions: [orGroup, andGroup], operator: '|' }
      : (orGroup ?? andGroup);

  if (positive && notGroup) return [{ conditions: [positive, notGroup], operator: '&' }];
  if (positive) return [positive];
  if (notGroup) return [notGroup];
  return [];
}

/**
 * Turn flat filter arguments into pan.do/ra conditions.
 *  - string  → `=` (contains, case-insensitive) — forgiving, which is what a
 *              model guessing at a director's name needs
 *  - number  → `==` (exact)
 *  - boolean → `==`
 *  - {from,to} → `>=` / `<=` (both documented in the API reference)
 */
function filterConditions(
  filters: Record<string, unknown>,
  allowed: Set<string>,
): { ok: true; conditions: PandoraCondition[] } | { ok: false; error: string } {
  const conditions: PandoraCondition[] = [];
  for (const [key, raw] of Object.entries(filters)) {
    if (!allowed.has(key)) {
      const list = [...allowed].slice(0, 40).join(', ');
      return {
        ok: false,
        error: `"${key}" is not a searchable field on this archive. Available fields: ${list}`,
      };
    }
    if (raw === null || raw === undefined) continue;
    if (typeof raw === 'string') {
      if (raw.trim()) conditions.push({ key, operator: '=', value: raw.trim() });
    } else if (typeof raw === 'number' || typeof raw === 'boolean') {
      conditions.push({ key, operator: '==', value: raw });
    } else if (typeof raw === 'object') {
      const range = raw as { from?: unknown; to?: unknown };
      if (typeof range.from === 'number')
        conditions.push({ key, operator: '>=', value: range.from });
      if (typeof range.to === 'number') conditions.push({ key, operator: '<=', value: range.to });
      if (range.from === undefined && range.to === undefined) {
        return {
          ok: false,
          error: `filter "${key}" must be a string, a number, or a range like {"from": 1970, "to": 1980}`,
        };
      }
    }
  }
  return { ok: true, conditions };
}

function firstArchive(ctx: ReadContext, requested: unknown): string | null {
  if (typeof requested === 'string' && requested) {
    return ctx.archives.some((a) => a.key === requested) ? requested : null;
  }
  return ctx.archives[0]?.key ?? null;
}

/**
 * Build the `itemsQuery` half of a moment search from the model's `filters` —
 * same fields, same validation and same self-correcting error as `search_items`.
 *
 * Deliberately NO playability guard, unlike `search_items`. `canplayvideo` is a
 * virtual key: the item index resolves it, but the annotation-side `itemsQuery`
 * cannot, so `canplayvideo == true` there matches *nothing* and silently empties
 * every moment search (verified against indiancine.ma: 42 rows without it, 0
 * with). `rightslevel` does evaluate, but its meaning is per-instance —
 * ctrl-z.in hardcodes `== 0` for pad.ma and `!= 5` for indiancine.ma — and
 * `site.rightsLevels` gives names, not a "streamable" flag, so any mapping
 * would be a guess. Moments are short clips that fetch fine regardless, so the
 * guard buys nothing worth an empty result set.
 */
async function itemsQueryFor(
  ctx: ReadContext,
  archive: string,
  filters: unknown,
): Promise<{ ok: true; itemsQuery?: ConditionGroup } | { ok: false; error: string }> {
  if (!filters || typeof filters !== 'object') return { ok: true };
  const keys = new Set(await ctx.port.itemKeys(archive));
  const allowed = new Set([...keys].filter((k) => !NON_FILTER_KEYS.has(k)));
  const built = filterConditions(filters as Record<string, unknown>, allowed);
  if (!built.ok) return built;
  return {
    ok: true,
    ...(built.conditions.length
      ? { itemsQuery: { conditions: built.conditions, operator: '&' as const } }
      : {}),
  };
}

function unknownArchive(ctx: ReadContext, requested: unknown): ToolResult {
  return toolError(
    `unknown archive "${String(requested)}" — available archives: ${ctx.archives
      .map((a) => a.key)
      .join(', ')}`,
  );
}

export function truncate(value: string, max: number): string {
  const clean = stripHtml(value).replace(/\s+/g, ' ').trim();
  return clean.length <= max ? clean : `${clean.slice(0, max - 1)}…`;
}

/** Annotation ids are "ITEM/AB"; the item id is the prefix. */
function annotationItem(id: string): string {
  const slash = id.indexOf('/');
  return slash > 0 ? id.slice(0, slash) : id;
}

// ---------------------------------------------------------------------------
// search_items
// ---------------------------------------------------------------------------

export const searchItemsTool: ReadToolDef = {
  name: 'search_items',
  kind: 'read',
  description:
    'Search an archive for FILMS/ITEMS by free text and metadata filters. Returns items, not timecodes — to find specific moments inside films, use search_moments instead. The filter fields available differ per archive and are listed in your instructions.',
  schema: {
    type: 'object',
    properties: {
      query: { type: 'string', description: 'free text, searched across all fields' },
      archive: { type: 'string', description: 'archive key; defaults to the first one' },
      filters: {
        type: 'object',
        description:
          'metadata filters, e.g. {"director": "Kumar", "year": {"from": 1970, "to": 1980}}',
      },
      limit: { type: 'integer', minimum: 1, maximum: 20 },
    },
  },
  describeCall: (a) => `Search items for ${JSON.stringify(a.query ?? '')}`,
  resultBudget: 4000,
  async run(args, ctx) {
    const archive = firstArchive(ctx, args.archive);
    if (!archive) return unknownArchive(ctx, args.archive);

    const keys = new Set(await ctx.port.itemKeys(archive));
    const conditions: ConditionNode[] = [];
    const text = typeof args.query === 'string' ? args.query.trim() : '';
    if (text) {
      // `*` alone is not enough, and on indiancine.ma it is actively wrong:
      // searching "Guddi" returns Adivasis and Alam Ara (it reaches into
      // annotation text) while missing the film actually called Guddi. OR in an
      // explicit title condition so a film named after the query is at least
      // IN the result set; ranking below then puts it at the top.
      conditions.push({
        conditions: [
          { key: 'title', operator: '=', value: text },
          { key: '*', operator: '=', value: text },
        ],
        operator: '|',
      });
    }

    if (args.filters && typeof args.filters === 'object') {
      const allowed = new Set([...keys].filter((k) => !NON_FILTER_KEYS.has(k)));
      const built = filterConditions(args.filters as Record<string, unknown>, allowed);
      if (!built.ok) return toolError(built.error);
      conditions.push(...built.conditions);
    }

    // No playability guard. `canplayvideo == true` cuts `title = Guddi` from 5
    // items to 1 on indiancine.ma, dropping the 1971 Guddi (rightslevel 2) —
    // a film whose moments search_moments returns and whose clips add_clip
    // places on the timeline perfectly well. Hiding material the rest of the
    // app can use is worse than occasionally naming something that will not
    // stream.

    const limit = typeof args.limit === 'number' ? args.limit : 10;
    let items;
    try {
      // Over-fetch so there is something for the ranking to reorder: the right
      // film can sit outside the first `limit` rows of a `modified` sort.
      const fetched = Math.min(Math.max(limit * 3, 20), 60);
      items = await ctx.port.findItems(archive, { conditions, operator: '&' }, [0, fetched]);
    } catch (err) {
      return toolError(`search failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!items.length) {
      return {
        ok: true,
        archive,
        items: [],
        note: 'no matches — try broader or different terms, or drop a filter',
      };
    }
    return {
      ok: true,
      archive,
      items: rankByTitle(items, text)
        .slice(0, limit)
        .map((i) => ({
          item: i.id,
          title: i.title,
          director: i.director?.join(', ') ?? null,
          year: i.year ?? null,
          duration: i.duration ?? null,
        })),
    };
  },
};

/**
 * Put films whose TITLE matches the query first.
 *
 * pan.do/ra has no relevance score — `find` sorts by whatever key you name, and
 * ours is `modified`, i.e. edit recency. For "Guddi" that ordered the film
 * called Guddi third, behind two films matching only on annotation text, and
 * the model took the top hit, searched the wrong film, found nothing and
 * retried the identical query eight times. Ranking is the cheap half of the
 * fix; the OR'd title condition above is the half that gets the film into the
 * set at all.
 *
 * Stable within each band, so the archive's own order still decides ties.
 */
function rankByTitle<T extends { title: string }>(items: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const band = (title: string): number => {
    const t = (title ?? '').toLowerCase();
    if (t === q) return 0;
    if (t.startsWith(q)) return 1;
    if (t.includes(q)) return 2;
    return 3;
  };
  return items
    .map((item, i) => ({ item, i, band: band(item.title) }))
    .sort((a, b) => a.band - b.band || a.i - b.i)
    .map((x) => x.item);
}

// ---------------------------------------------------------------------------
// search_moments — the primary clip-finding tool
// ---------------------------------------------------------------------------

export const searchMomentsTool: ReadToolDef = {
  name: 'search_moments',
  kind: 'read',
  description:
    'Search annotations (transcripts, subtitles, keywords, AI shot descriptions in the machinedescriptions layer) across an archive for MOMENTS. ' +
    'This is the main way to find footage: each result carries real in/out times in CONCAT seconds, which you can pass straight to add_clip. ' +
    'Every match is shown to the user as clip cards automatically — you get a summary plus a sample, and do NOT need to repeat them with show_clips. ' +
    'In `query`, `+` means AND, `|` means OR and `!` means NOT: "red+carpet|rug !wedding".',
  schema: {
    type: 'object',
    properties: {
      query: {
        type: 'string',
        description: 'free text; + AND, | OR, ! NOT — e.g. "red+carpet|rug !wedding"',
      },
      archive: { type: 'string' },
      item: { type: 'string', description: 'restrict to one item id, or several joined with +' },
      layer: { type: 'string', description: 'restrict to one annotation layer' },
      filters: {
        type: 'object',
        description:
          'metadata filters on the FILM the moment is in — same fields as search_items, e.g. {"year": {"from": 1970, "to": 1980}, "director": "Kumar"}',
      },
      limit: { type: 'integer', minimum: 1, maximum: MAX_MOMENT_LIMIT },
    },
    required: ['query'],
  },
  describeCall: (a) => {
    const where = a.filters ? ` ${JSON.stringify(a.filters)}` : '';
    return `Search moments for ${JSON.stringify(a.query ?? '')}${where}`;
  },
  resultBudget: 3000,
  async run(args, ctx) {
    const archive = firstArchive(ctx, args.archive);
    if (!archive) return unknownArchive(ctx, args.archive);
    const limit = Math.min(
      Math.max(typeof args.limit === 'number' ? Math.floor(args.limit) : DEFAULT_MOMENT_LIMIT, 1),
      MAX_MOMENT_LIMIT,
    );

    const conditions: ConditionNode[] = valueConditions(
      parseQueryTerms(typeof args.query === 'string' ? args.query : ''),
    );
    if (typeof args.layer === 'string' && args.layer.trim()) {
      conditions.push({ key: 'layer', operator: '==', value: args.layer.trim() });
    }
    // `item` narrows server-side now. It used to be a client-side filter over a
    // 10x-widened page, which meant a search inside one film paid for ten pages
    // of another film's annotations and still ran out of matches early.
    const items = typeof args.item === 'string' ? splitIds(args.item) : [];
    if (items.length === 1) {
      conditions.push({ key: 'item', operator: '==', value: items[0] });
    } else if (items.length > 1) {
      conditions.push({
        conditions: items.map((id) => ({ key: 'item', operator: '==', value: id })),
        operator: '|',
      });
    }
    if (!conditions.length) {
      return toolError('give me something to search for: a query, a layer, or an item');
    }

    const built = await itemsQueryFor(ctx, archive, args.filters);
    if (!built.ok) return toolError(built.error);
    const query: ConditionGroup = { conditions, operator: '&' };
    const scope = { query, ...(built.itemsQuery ? { itemsQuery: built.itemsQuery } : {}) };

    // Over-fetch by exactly one row. That is the whole "is there more?"
    // mechanism: `findAnnotations` with no `keys` returns HTTP 500 on the live
    // instances, and counting by asking for ids alone is not cheap here — a
    // broad term over the shot-description corpus is 48k rows and ~900KB, on
    // someone else's server, on every search. One spare row costs nothing and
    // gives an exact count whenever the result set fits under the limit, which
    // is the ordinary case.
    let page: ArchiveAnnotation[];
    try {
      page = await ctx.port.findAnnotations(archive, { ...scope, range: [0, limit + 1] });
    } catch (err) {
      return toolError(`search failed: ${err instanceof Error ? err.message : String(err)}`);
    }
    const more = page.length > limit;
    const rows = more ? page.slice(0, limit) : page;

    if (!rows.length) {
      return {
        ok: true,
        archive,
        total: 0,
        moments: [],
        note: 'no matches — try different words, drop a filter, or use search_items to find relevant films first',
      };
    }

    const cards = rows.slice(0, MAX_MOMENT_CARDS).map((a) => ({
      archive,
      item: annotationItem(a.id),
      title: a.title ?? annotationItem(a.id),
      in: round(a.in),
      out: round(a.out),
      note: truncate(a.value, CARD_NOTE_CHARS),
    }));

    // The user gets every card; the model gets a digest. Handing the model the
    // full set and asking it to call show_clips makes it the thing that decides
    // what to drop, on a context budget far smaller than the result set.
    // `more` also covers the CRDT card cap: if the attachment had to drop rows,
    // the header must not claim to be showing everything either.
    const truncated = more || cards.length < rows.length;
    ctx.postChat({
      body: '',
      atts: [
        {
          type: 'moments',
          label: momentLabel(archive, args),
          total: rows.length,
          ...(truncated ? { more: true } : {}),
          clips: cards,
        },
      ],
    });

    return {
      ok: true,
      archive,
      total: rows.length,
      ...(truncated ? { more: true } : {}),
      shown_to_user: cards.length,
      films: groupFilms(rows),
      sample: sampleAcrossFilms(rows).map((a) => ({
        item: annotationItem(a.id),
        in: round(a.in),
        out: round(a.out),
        layer: a.layer ?? null,
        text: truncate(a.value, VALUE_CHARS),
      })),
      note:
        `${cards.length} clip cards are already shown to the user` +
        (truncated ? ', and the archive has more — narrow the query or raise the limit' : '') +
        '. Do not repeat them with show_clips. `sample` is a spread you can add_clip from directly; ' +
        'use the exact in/out shown.',
    };
  },
};

/** "A+B+C" → ["A","B","C"], the multi-item form ctrl-z.in uses. */
function splitIds(raw: string): string[] {
  return raw
    .split('+')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** One line naming the search, for the header above the cards. */
function momentLabel(archive: string, args: Record<string, unknown>): string {
  const bits = [`“${String(args.query ?? '').trim()}”`];
  if (args.layer) bits.push(`layer ${String(args.layer)}`);
  if (args.item) bits.push(`in ${String(args.item)}`);
  if (args.filters && typeof args.filters === 'object') {
    for (const [key, value] of Object.entries(args.filters as Record<string, unknown>)) {
      if (value && typeof value === 'object') {
        const r = value as { from?: unknown; to?: unknown };
        bits.push(`${key} ${r.from ?? ''}–${r.to ?? ''}`);
      } else if (value !== undefined && value !== null && value !== '') {
        bits.push(`${key} ${String(value)}`);
      }
    }
  }
  bits.push(archive);
  return bits.join(' · ');
}

/** Per-film counts, so the model can see the shape of a result set it never reads. */
function groupFilms(rows: ArchiveAnnotation[]): { item: string; title: string; count: number }[] {
  const by = new Map<string, { item: string; title: string; count: number }>();
  for (const a of rows) {
    const item = annotationItem(a.id);
    const hit = by.get(item);
    if (hit) hit.count += 1;
    else by.set(item, { item, title: a.title ?? item, count: 1 });
  }
  return [...by.values()].sort((a, b) => b.count - a.count);
}

/**
 * Round-robin one row at a time from each film. Results arrive sorted by item
 * then time, so slicing the first N would hand the model ten moments from one
 * film and pretend that was the whole search.
 */
function sampleAcrossFilms(rows: ArchiveAnnotation[]): ArchiveAnnotation[] {
  const byItem = new Map<string, ArchiveAnnotation[]>();
  for (const a of rows) {
    const item = annotationItem(a.id);
    const list = byItem.get(item);
    if (list) list.push(a);
    else byItem.set(item, [a]);
  }
  const lists = [...byItem.values()];
  const out: ArchiveAnnotation[] = [];
  for (let i = 0; out.length < SAMPLE_ROWS; i += 1) {
    let added = false;
    for (const list of lists) {
      if (i >= list.length) continue;
      out.push(list[i]);
      added = true;
      if (out.length >= SAMPLE_ROWS) break;
    }
    if (!added) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// get_item
// ---------------------------------------------------------------------------

export const getItemTool: ReadToolDef = {
  name: 'get_item',
  kind: 'read',
  description:
    'Exact metadata for one item, including its total duration and per-part durations. Use this to check valid in/out ranges before trimming near the end of a film.',
  schema: {
    type: 'object',
    properties: { archive: { type: 'string' }, item: { type: 'string' } },
    required: ['item'],
  },
  describeCall: (a) => `Look up item ${a.item}`,
  resultBudget: 800,
  async run(args, ctx) {
    const archive = firstArchive(ctx, args.archive);
    if (!archive) return unknownArchive(ctx, args.archive);
    try {
      const detail = await ctx.port.getItem(archive, args.item as string);
      const durations = detail.durations ?? [];
      return {
        ok: true,
        archive,
        item: detail.id,
        title: detail.title,
        director: detail.director?.join(', ') ?? null,
        year: detail.year ?? null,
        duration: detail.duration ?? durations.reduce((a, b) => a + b, 0),
        parts: durations.length,
        durations: durations.map(round),
      };
    } catch (err) {
      return toolError(
        `could not load "${String(args.item)}" from ${archive}: ${
          err instanceof Error ? err.message : String(err)
        }. Check the id with search_items.`,
      );
    }
  },
};

// ---------------------------------------------------------------------------
// get_transcript
// ---------------------------------------------------------------------------

export const getTranscriptTool: ReadToolDef = {
  name: 'get_transcript',
  kind: 'read',
  description:
    'Read the time-aligned transcript/subtitles of an item, optionally windowed to a time range (CONCAT seconds). Use it to find an exact quote or to check what is said around a moment.',
  schema: {
    type: 'object',
    properties: {
      archive: { type: 'string' },
      item: { type: 'string' },
      from: { type: 'number', minimum: 0 },
      to: { type: 'number', minimum: 0 },
    },
    required: ['item'],
  },
  describeCall: (a) => `Read transcript of ${a.item}`,
  resultBudget: TRANSCRIPT_CHARS + 500,
  async run(args, ctx) {
    const archive = firstArchive(ctx, args.archive);
    if (!archive) return unknownArchive(ctx, args.archive);
    let layers: Record<string, ArchiveAnnotation[]>;
    let subtitleLayers: string[];
    try {
      [layers, subtitleLayers] = await Promise.all([
        ctx.port.getLayers(archive, args.item as string),
        ctx.port.subtitleLayers(archive),
      ]);
    } catch (err) {
      return toolError(
        `could not load transcript for "${String(args.item)}": ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }

    const from = typeof args.from === 'number' ? args.from : -Infinity;
    const to = typeof args.to === 'number' ? args.to : Infinity;
    const rows = subtitleLayers
      .flatMap((id) => layers[id] ?? [])
      .filter((a) => a.out >= from && a.in <= to)
      .sort((a, b) => a.in - b.in);

    if (!rows.length) {
      const available = Object.keys(layers).filter((k) => (layers[k] ?? []).length);
      return {
        ok: true,
        item: args.item,
        lines: [],
        note: available.length
          ? `no transcript in that range. Layers with content: ${available.join(', ')}`
          : 'this item has no transcript',
      };
    }

    // Fill up to the character budget and say plainly how much was left out —
    // a model that silently sees half a transcript will reason as if it saw all.
    const lines: { in: number; out: number; text: string }[] = [];
    let used = 0;
    for (const row of rows) {
      const text = truncate(row.value, VALUE_CHARS);
      const cost = text.length + LINE_ENVELOPE_CHARS;
      if (used + cost > TRANSCRIPT_CHARS) break;
      used += cost;
      lines.push({ in: round(row.in), out: round(row.out), text });
    }
    const result: ToolResult = { ok: true, item: args.item, lines };
    if (lines.length < rows.length) {
      (result as Record<string, unknown>).truncated =
        `showing ${lines.length} of ${rows.length} lines; narrow the range with from/to to see more`;
    }
    return result;
  },
};

// ---------------------------------------------------------------------------
// inspect_timeline / list_bin
// ---------------------------------------------------------------------------

export const inspectTimelineTool: ReadToolDef = {
  name: 'inspect_timeline',
  kind: 'read',
  description:
    'The current state of the edit you are working on: tracks, clip ids, timings and properties. Call this before editing anything you did not just create yourself. ' +
    'Long tracks elide their middle ("… N more clips …"); pass from/to (timeline seconds) to list the clips in that range instead — you need this to get the ids of elided clips.',
  schema: {
    type: 'object',
    properties: {
      from: {
        type: 'number',
        description: 'start of the time window (timeline seconds)',
      },
      to: {
        type: 'number',
        description: 'end of the time window (timeline seconds)',
      },
    },
  },
  describeCall: (args) =>
    args.from !== undefined || args.to !== undefined
      ? `Inspect the timeline (${Number(args.from ?? 0)}s–${args.to !== undefined ? `${Number(args.to)}s` : 'end'})`
      : 'Inspect the timeline',
  resultBudget: 8000,
  async run(args, ctx) {
    const hasWindow = args.from !== undefined || args.to !== undefined;
    const window = hasWindow
      ? { from: Number(args.from ?? 0), to: args.to !== undefined ? Number(args.to) : Infinity }
      : undefined;
    return {
      ok: true,
      timeline: describeEdit(ctx.draft(), {
        playhead: ctx.live.playhead,
        selection: ctx.live.selection,
        window,
      }),
    };
  },
};

export const listBinTool: ReadToolDef = {
  name: 'list_bin',
  kind: 'read',
  description: "Clips the user has saved to their bin — material they've already picked out.",
  schema: { type: 'object', properties: {} },
  describeCall: () => 'List the bin',
  resultBudget: 2000,
  async run(_args, ctx) {
    return {
      ok: true,
      bin: ctx.live.bin.map((entry) => ({
        title: entry.title,
        archive: entry.archive ?? null,
        item: entry.item ?? null,
        in: entry.in !== undefined ? round(entry.in) : null,
        out: entry.out !== undefined ? round(entry.out) : null,
      })),
    };
  },
};

export const READ_TOOLS: ReadToolDef[] = [
  searchItemsTool,
  searchMomentsTool,
  getItemTool,
  getTranscriptTool,
  inspectTimelineTool,
  listBinTool,
];

/**
 * Three decimals, because that is what the archive actually stores: pan.do/ra
 * serves annotation times to the millisecond and names clips `ITEM/0.000-405.593`.
 * One decimal — what this used to do — collapses a frame-length annotation
 * (5173.72 → 5173.76) into `in === out`, which every downstream gate then
 * rejects as an empty range. The model's only escape from that error was to
 * invent a duration, which is exactly what it did.
 */
export function round(n: number): number {
  return Math.round(n * 1000) / 1000;
}
